import React from 'react';

import { CrossIcon } from '@vlabs/icons';
import { AttachCardsToListButton } from '@vlabs/monokit/features/attach-cards-to-list';
import { DeleteCardsButton } from '@vlabs/monokit/features/delete-cards';
import { DetachCardsFromListButton } from '@vlabs/monokit/features/detach-cards-from-list';
import { MoveCardsToListButton } from '@vlabs/monokit/features/move-cards-to-list';
import type { CardId } from '@vlabs/api/point-api/model';
import type { BulkActionsProps } from '@vlabs/uikit';
import { BulkActions } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { useTranslation } from 'react-i18next';

import st from './CardsBulkActions.module.sass';
import { useListIdFromLocation } from '../../../useListIdFromLocation';

export interface CardsBulkActionsProps extends BulkActionsProps {
  selectedCardIds: CardId[];
  onCancel: VoidFunction;
}

function CardsBulkActions({
  selectedCardIds,
  onCancel,
  onCheckboxClick,
  indeterminate = false,
}: CardsBulkActionsProps): React.JSX.Element {
  const { t } = useTranslation();
  const listId = useListIdFromLocation();

  return (
    <BulkActions
      data-testid="CardsBulkActions"
      indeterminate={indeterminate}
      onCheckboxClick={onCheckboxClick}
    >
      <div className={st.Root}>
        <span
          className={st.SelectedCardsCount}
          data-testid="CardsBulkActions-SelectedCount"
        >
          {t('list-details-page:cards_bulk_actions.selected_count', { count: (selectedCardIds?.length ?? 0) })}
        </span>
        <div className={st.Controls}>
          <AttachCardsToListButton
            cardIds={selectedCardIds}
            size="small"
            variant="flat-inverted"
          />
          <MoveCardsToListButton
            cardIds={selectedCardIds}
            fromListId={listId}
            size="small"
            variant="flat-inverted"
          />
          <DetachCardsFromListButton
            cardIds={selectedCardIds}
            fromListId={listId}
            size="small"
            variant="flat-inverted"
          />
          <DeleteCardsButton
            cardIds={selectedCardIds}
            size="small"
            variant="flat-inverted"
          />
          <IconButton
            data-testid="CardsBulkActions-CancelBtn"
            icon={<CrossIcon />}
            kind="secondary"
            onClick={onCancel}
            shape="square"
            size="small"
            variant="flat-inverted"
          />
        </div>
      </div>
    </BulkActions>
  );
}

export { CardsBulkActions };
